import React, { useEffect, useMemo } from 'react';
import { X } from 'lucide-react';
import { scoreColor } from './companyScore';

// Lightweight per-category chip styles (self-contained — the macro modal pulls
// these from indicatorFormat, but the Company Score categories are different).
const CATEGORY_STYLES = {
  'Fair Value Upside': { bg: 'bg-emerald-900/40', text: 'text-emerald-300' },
  Valuation: { bg: 'bg-blue-900/40', text: 'text-blue-300' },
  Profitability: { bg: 'bg-purple-900/40', text: 'text-purple-300' },
  Growth: { bg: 'bg-cyan-900/40', text: 'text-cyan-300' },
  'Estimate Revisions': { bg: 'bg-amber-900/40', text: 'text-amber-300' },
  Technicals: { bg: 'bg-rose-900/40', text: 'text-rose-300' },
};

function fmtRaw(raw, unit) {
  if (raw == null) return '—';
  const n = typeof raw === 'number' ? Math.round(raw * 100) / 100 : raw;
  if (!unit || unit === 'ratio' || unit === 'index') return String(n);
  if (unit === 'x') return `${n}x`;
  if (unit.startsWith('%')) {
    const rest = unit.slice(1).trim();
    return rest ? `${n}% ${rest}` : `${n}%`;
  }
  return `${n} ${unit}`;
}

function pct(n) {
  return n == null ? '—' : `${Math.round(n)}`;
}

// One ticker's per-signal breakdown. `result` is the live, user-weighted
// aggregate (companyScore.aggregate) — so it matches the gauge exactly.
function BreakdownTable({ title, result, meta }) {
  if (!result) return null;
  // Show only the features included in this stock's method (weight > 0).
  const signals = (result.signals || []).filter((s) => (s.weight || 0) > 0);
  const totalWeight =
    signals.reduce((s, x) => s + (x.weight > 0 && x.subscore != null ? x.weight : 0), 0) || 1;
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h4 className="text-sm font-semibold text-white">{title}</h4>
        <span className="text-sm font-bold" style={{ color: scoreColor(result.value) }}>
          {result.value == null ? 'No data' : `${Math.round(result.value)} · ${result.label}`}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-gray-500 border-b border-gray-800">
              <th className="py-2 pr-3 text-left font-medium">Signal</th>
              <th className="py-2 pr-3 text-left font-medium">Method</th>
              <th className="py-2 pr-3 text-right font-medium">Latest</th>
              <th className="py-2 pr-3 text-right font-medium">Sub-score</th>
              <th className="py-2 pr-3 text-right font-medium">Weight</th>
              <th className="py-2 pr-2 text-right font-medium">Contribution</th>
            </tr>
          </thead>
          <tbody>
            {signals.map((s) => {
              const m = meta?.[s.key] || {};
              const cat = CATEGORY_STYLES[s.category];
              const wPct = s.subscore != null && s.weight > 0 ? (s.weight / totalWeight) * 100 : null;
              return (
                <tr key={s.key} className="border-b border-gray-800/60">
                  <td className="py-2 pr-3">
                    <div className="text-gray-100">{s.label}</div>
                    <div className="flex items-center gap-1 mt-0.5">
                      {cat && (
                        <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded ${cat.bg} ${cat.text}`}>
                          {s.category}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 pr-3 text-gray-400 text-xs">{s.method}</td>
                  <td className="py-2 pr-3 text-right text-gray-200">{fmtRaw(s.raw, m.unit)}</td>
                  <td className="py-2 pr-3 text-right font-semibold">
                    {s.subscore == null ? (
                      <span className="text-gray-600 italic text-xs">no data</span>
                    ) : (
                      <span style={{ color: scoreColor(s.subscore) }}>{Math.round(s.subscore)}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right text-gray-300">{wPct == null ? '—' : `${Math.round(wPct)}%`}</td>
                  <td className="py-2 pr-2 text-right text-gray-300">{pct(s.contribution)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {result.degraded && result.value != null && (
        <p className="text-[11px] text-yellow-500/80 mt-1">
          Based on {Math.round(result.coverage * 100)}% of intended signals (others lacked data).
        </p>
      )}
    </section>
  );
}

function CompanyScoreMethodologyModal({ isOpen, onClose, title, result, methodology }) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const meta = methodology?.signals || {};
  const methods = methodology?.methods || {};

  const anchoredSignals = useMemo(
    () =>
      Object.entries(meta)
        .filter(([, info]) => Array.isArray(info.anchors) && info.anchors.length)
        .map(([key, info]) => ({ key, ...info })),
    [meta],
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-gray-900 border border-gray-700 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between px-5 py-4 border-b border-gray-700 bg-gray-900">
          <div>
            <h3 className="text-lg font-semibold text-white">How the Company Score is calculated</h3>
            <p className="text-xs text-gray-500">
              Per-signal breakdown for {title || 'the selected stock'} reflects your current weights.
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-6">
          <section>
            <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2">How it works</h4>
            <ol className="list-decimal list-inside text-sm text-gray-300 space-y-1">
              <li>Each metric's latest reading is normalized to a <strong>0–100 sub-score</strong> (higher = a more attractive buy) using the method shown per row.</li>
              <li>Sub-scores are combined into the headline as a <strong>weighted average</strong>.</li>
            </ol>
            {methodology?.weighting_note && (
              <p className="text-xs text-gray-500 mt-2">{methodology.weighting_note}</p>
            )}
          </section>

          <BreakdownTable title={title || 'Company Score'} result={result} meta={meta} />

          <section>
            <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2">Normalization methods</h4>
            {Object.keys(methods).length === 0 ? (
              <p className="text-xs text-gray-500 italic">Method details unavailable — re-run the analysis.</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(methods).map(([name, desc]) => (
                  <div key={name} className="text-sm">
                    <span className="text-gray-200 font-semibold capitalize">{name}</span>
                    <span className="text-gray-400"> — {desc}</span>
                  </div>
                ))}
              </div>
            )}
            {anchoredSignals.length > 0 && (
              <div className="mt-3">
                <h5 className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Thresholds (value → score)</h5>
                <div className="space-y-1">
                  {anchoredSignals.map((sig) => (
                    <div key={sig.key} className="text-xs">
                      <span className="text-gray-300">{sig.label}</span>
                      <span className="text-gray-500">
                        {': '}
                        {sig.anchors.map(([x, sc]) => `${x}→${sc}`).join('  ·  ')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>

        <div className="px-5 py-3 border-t border-gray-800 text-[11px] text-gray-500">
          Sub-score, weight, and contribution above match the gauge with your current weights.
        </div>
      </div>
    </div>
  );
}

export default CompanyScoreMethodologyModal;
